import { Drawer } from '@mantine/core';
import { Icon } from './Icon';
import { attrTypeIcon, attrTypeLabel, sourceLabel, formatDateTime } from '@/lib/format';
import type { EventRecord } from '@/api/entrepreneurs';
import type { IconName } from './Icon';

type EventDrawerProps = {
  event: EventRecord | null;
  onClose: () => void;
};

/**
 * Right-side drawer showing the full payload of a journal event. Uses
 * inline styles for the header/body chrome so the layout is unaffected
 * by Mantine's higher-specificity wrappers.
 */
export function EventDrawer({ event, onClose }: EventDrawerProps) {
  return (
    <Drawer
      opened={event != null}
      onClose={onClose}
      position="right"
      size={540}
      withCloseButton={false}
      padding={0}
      transitionProps={{ duration: 220 }}
    >
      {event && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--paper)',
          }}
        >
          <header
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: '20px 22px',
              borderBottom: '1px solid var(--line)',
              background: 'var(--panel)',
              flex: '0 0 auto',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'grid',
                placeItems: 'center',
                background: 'var(--panel)',
                border: '1px solid var(--line-2)',
                color: 'var(--accent-2)',
                boxShadow: 'var(--sh-sm)',
                flex: '0 0 auto',
              }}
            >
              <Icon name={attrTypeIcon(event.attributeType) as IconName} size={19} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-.02em' }}>
                {attrTypeLabel(event.attributeType)}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontWeight: 600, marginTop: 3 }}>
                {sourceLabel(event.sourceSystem)}
                {event.sourceActorName ? ` · ${event.sourceActorName}` : ''}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--panel)',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                color: 'var(--ink-2)',
                boxShadow: 'var(--sh-sm)',
                flex: '0 0 auto',
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </header>

          <div
            style={{
              flex: '1 1 auto',
              overflowY: 'auto',
              padding: '22px 24px',
              minHeight: 0,
            }}
          >
            <div className="card pad" style={{ marginBottom: 18 }}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 14, letterSpacing: '-.01em' }}>
                Traçabilité
              </div>
              <dl className="kv">
                <dt>Source</dt>
                <dd>{sourceLabel(event.sourceSystem)}</dd>
                <dt>Acteur</dt>
                <dd style={{ fontFamily: 'var(--mono)' }}>{event.sourceActorId || '—'}</dd>
                <dt>Survenu le</dt>
                <dd style={{ fontFamily: 'var(--mono)' }}>{formatDateTime(event.occurredAt)}</dd>
                <dt>Enregistré le</dt>
                <dd style={{ fontFamily: 'var(--mono)' }}>{formatDateTime(event.recordedAt)}</dd>
                <dt>Version</dt>
                <dd style={{ fontFamily: 'var(--mono)' }}>{event.schemaVersion}</dd>
                <dt>Référence</dt>
                <dd style={{ fontFamily: 'var(--mono)', fontSize: 11.5, wordBreak: 'break-all' }}>
                  {event.eventId}
                </dd>
              </dl>
            </div>

            <div
              style={{
                fontWeight: 800,
                fontSize: 13,
                marginBottom: 10,
                letterSpacing: '-.01em',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon name="cmd" size={15} style={{ color: 'var(--ink-3)' }} />
              Données brutes
              <span className="meta" style={{ marginLeft: 'auto' }}>
                mode développeur
              </span>
            </div>
            <PayloadJSON data={event.payload} />
          </div>
        </div>
      )}
    </Drawer>
  );
}

function PayloadJSON({ data }: { data: unknown }) {
  const text = JSON.stringify(data, null, 2) ?? 'null';
  return (
    <pre
      style={{
        font: '600 12px/1.65 var(--mono)',
        background: 'var(--ink)',
        color: 'oklch(0.92 0.01 200)',
        borderRadius: 12,
        padding: '16px 18px',
        overflowX: 'auto',
        whiteSpace: 'pre',
      }}
    >
      {text}
    </pre>
  );
}
